import { Stack } from '@startuptools/ui/Stack';
import { GqlMeeting, GqlMeetingConcern, GqlShareIssue } from '../../../../graphql/base-types.graphql';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import { ShareIssueDocument } from '../../ShareIssue.graphql';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { DateInput } from '@startuptools/ui/custom/DateInput';
import { Alert } from '@startuptools/ui/Alert';
import { maxBy } from 'lodash-es';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';
import { useSiSetSubscriptionDatesMutation } from '../../../../graphql/react-operations';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';

type FormValues = {
  shareSubscribeStartDate: DateTime;
  shareSubscribeEndDate: DateTime;
};

export const SiStepSubscriptionDate = ({
  shareIssue,
}: {
  shareIssue: Pick<
    GqlShareIssue,
    'id' | 'subscriptionStartDate' | 'subscriptionEndDate' | 'preferentialRights' | 'companyId'
  > & {
    meetingConcern?: (Pick<GqlMeetingConcern, 'id'> & { meeting: Pick<GqlMeeting, 'id' | 'startTime'> }) | null;
  };
}) => {
  const { validatorStatus } = useStepperStepContent();
  const { stepNext } = useStepper();
  const { i18n } = useLingui();
  const [doSetDate, { loading }] = useSiSetSubscriptionDatesMutation({ refetchQueries: [ShareIssueDocument] });
  const meeting = shareIssue.meetingConcern?.meeting;
  const defaultStartDate = shareIssue.subscriptionStartDate
    ? DateTime.fromISO(shareIssue.subscriptionStartDate).startOf('day')
    : meeting
      ? DateTime.fromISO(meeting.startTime).startOf('day')
      : DateTime.now().plus({ day: 14 }).startOf('day');

  const { control, watch, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      shareSubscribeStartDate: defaultStartDate,
      shareSubscribeEndDate: shareIssue.subscriptionEndDate
        ? DateTime.fromISO(shareIssue.subscriptionEndDate).startOf('day')
        : defaultStartDate.plus({ days: 14 }),
    },
  });

  const currentStartDate = watch('shareSubscribeStartDate');
  const minDate =
    shareIssue.preferentialRights && meeting?.startTime
      ? maxBy([currentStartDate, DateTime.fromISO(meeting.startTime).plus({ days: 14 })], d => d.toSeconds())
      : currentStartDate;

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSetDate({
        variables: {
          companyId: shareIssue.companyId,
          input: {
            id: shareIssue.id,
            subscriptionStartDate: values.shareSubscribeStartDate.toUTC(0, { keepLocalTime: true }).toString(),
            subscriptionEndDate: values.shareSubscribeEndDate.toUTC(0, { keepLocalTime: true }).toString(),
          },
        },
      });
      return stepNext();
    } catch (e) {
      captureException(e);
      genericErrorToast();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Choose first and last subscription date" />
        </Typography>

        <Alert>
          <Trans id="Set the first and last day to subscribe." />
        </Alert>

        <Controller
          control={control}
          name="shareSubscribeStartDate"
          rules={{
            required: true,
            validate: d => {
              if (meeting?.startTime && DateTime.fromISO(meeting.startTime).startOf('day') > d) {
                return i18n._({ id: 'Can not be before meeting date' });
              }
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ maxWidth: 300 }} error={Boolean(error)} required>
              <FormLabel>
                <Trans id="First day to subscribe" />
              </FormLabel>
              <DateInput
                {...field}
                minDate={meeting?.startTime ? DateTime.fromISO(meeting.startTime).startOf('day') : undefined}
              />
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="shareSubscribeEndDate"
          rules={{
            required: true,
            validate: d => {
              if (minDate && d < minDate) {
                return shareIssue.preferentialRights
                  ? i18n._({ id: 'Must be at least 14 days after start date' })
                  : i18n._({ id: 'Can not be before start date' });
              }
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ maxWidth: 300 }} error={Boolean(error)} required>
              <FormLabel>
                <Trans id="Last day to subscribe" />
              </FormLabel>
              <DateInput {...field} minDate={minDate} />
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          )}
        />

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
